import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { LogOut, Video } from 'lucide-react';
import { toast } from 'sonner';

export const CandidateDashboard = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.userType !== 'candidate') {
      navigate('/interview-portal');
      return;
    }
    fetchInterviews();
  }, [user, authLoading, navigate]);

  const fetchInterviews = async () => {
    try {
      const response = await api.get('/candidate/interviews');
      setInterviews(response.data);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInterview = (interviewId) => {
    navigate(`/interview-portal/${interviewId}`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p>Loading...</p>
      </div>
    );
  }

  if (!user || user.userType !== 'candidate') {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-light tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>
              Welcome, {user?.userName}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Your assigned interviews</p>
          </div>
          <Button
            variant="outline"
            onClick={logout}
            data-testid="candidate-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {interviews.length === 0 ? (
          <div className="text-center py-12" data-testid="no-interviews-message">
            <Video className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg text-slate-600">No interviews assigned yet</p>
            <p className="text-sm text-slate-500 mt-2">Please contact your administrator</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {interviews.map((interview) => (
              <div
                key={interview.id}
                className="bg-white border border-slate-200 rounded-md p-6 hover:-translate-y-1 transition-transform duration-200"
                data-testid={`interview-card-${interview.id}`}
              >
                <h3 className="text-xl font-medium mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>
                  {interview.name}
                </h3>
                <Button
                  onClick={() => handleOpenInterview(interview.id)}
                  className="w-full"
                  data-testid={`open-interview-button-${interview.id}`}
                >
                  Open Interview
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
